import { type PropsWithChildren, type ReactNode, useState } from 'react';
import { useSegment } from '@repo/poly-hooks/segment';
import { isUnsupportedAndroid, isUnsupportedIOS } from '../../utils/fourthline';
import { simulateLinkClick } from '../../utils/simulate-link-click';
import { browser, isMobile, isWebView, os } from '../../utils/device';
import { Unsupported } from './unsupported';
import { Pending } from './pending';
import { LinkToMobile } from './link-to-mobile';
import { Reassurance } from './reassurance';
import { EmbeddedWebsdk, type EmbeddedWebsdkProps, EmbeddedWebSDKTest } from './embedded-websdk';
import styles from './fourthline.module.css';

interface BaseFourthlineProps {
  fourthlineRedirectUrl: string;
  isKycExpired?: boolean;
  isKycRequired: boolean;
  isQesRequired: boolean;
  legalCountry: string;
  mode?: string;
  onBack?: () => void;
}

export type EmbeddedWebSdk = Omit<EmbeddedWebsdkProps, 'visible'>;

interface EmbeddedWebSDKProps extends BaseFourthlineProps {
  embeddedWebSdkProps: EmbeddedWebSdk;
  onSendSMS?: never;
  onRegenerateLink?: never;
  isLoading?: never;
}

interface LegacyFlowProps extends BaseFourthlineProps {
  embeddedWebSdkProps?: never;
  onSendSMS: () => void;
  onRegenerateLink: () => void;
  isLoading?: boolean;
}

export type FourthlineProps = EmbeddedWebSDKProps | LegacyFlowProps;

function Wrapper({ children }: PropsWithChildren): ReactNode {
  return <div className={styles.wrapper}>{children}</div>;
}

export function Fourthline({
  mode = 'verification',
  onBack,
  isKycRequired,
  isKycExpired,
  isQesRequired,
  fourthlineRedirectUrl,
  legalCountry,
  embeddedWebSdkProps,
  onSendSMS,
  onRegenerateLink,
  isLoading,
}: FourthlineProps): ReactNode {
  const [displayReassurance, setDisplayReassurance] = useState(true);
  const segment = useSegment();

  const isAndroid = os?.name === 'Android';
  const isIOS = os?.name === 'iOS';
  const isAndroidAndIsUnsupported =
    isAndroid && isUnsupportedAndroid(browser?.name, browser?.version);
  const isIosAndIsUnsupported =
    isIOS && isUnsupportedIOS(browser?.name, browser?.version, os?.version);

  const onReassuranceContinue = (): void => {
    if (embeddedWebSdkProps) {
      segment.track(`${mode}_fourthline_started_websdk`, {
        domain: window.location.hostname,
        isMobile,
        ...(embeddedWebSdkProps.eventStartedProperties ?? {}),
      });
    }

    if (isWebView) {
      simulateLinkClick({
        href: fourthlineRedirectUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
      });
      return;
    }

    if (isMobile) {
      window.location.replace(fourthlineRedirectUrl);
      return;
    }

    setDisplayReassurance(false);
  };

  const turnOnDisplayReassurance = (): void => {
    setDisplayReassurance(true);
  };

  if ((isIosAndIsUnsupported || isAndroidAndIsUnsupported) && !isWebView) {
    return (
      <Wrapper>
        <Unsupported fourthlineRedirectUrl={fourthlineRedirectUrl} mode={mode} />
      </Wrapper>
    );
  }

  if (isWebView && !displayReassurance && !embeddedWebSdkProps) {
    return (
      <Wrapper>
        <Pending
          fourthlineRedirectUrl={fourthlineRedirectUrl}
          onBack={turnOnDisplayReassurance}
          onRegenerateLink={onRegenerateLink}
        />
      </Wrapper>
    );
  }

  if (!isMobile && !displayReassurance && !embeddedWebSdkProps) {
    return (
      <Wrapper>
        <LinkToMobile
          fourthlineRedirectUrl={fourthlineRedirectUrl}
          isKycRequired={isKycRequired}
          isLoading={isLoading}
          isQesRequired={isQesRequired}
          mode={mode}
          onBack={turnOnDisplayReassurance}
          onRegenerateLink={onRegenerateLink}
          onSendSMS={onSendSMS}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {displayReassurance ? (
        <Reassurance
          isKycExpired={isKycExpired}
          isKycRequired={isKycRequired}
          isQesRequired={isQesRequired}
          legalCountry={legalCountry}
          mode={mode}
          onBack={onBack}
          onContinue={onReassuranceContinue}
        />
      ) : null}
      {embeddedWebSdkProps && !isMobile && !isWebView && process.env.KYC_TEST === undefined ? (
        <EmbeddedWebsdk visible={!displayReassurance} {...embeddedWebSdkProps} />
      ) : null}
      {embeddedWebSdkProps && !isMobile && !isWebView && process.env.KYC_TEST !== undefined ? (
        <EmbeddedWebSDKTest visible={!displayReassurance} {...embeddedWebSdkProps} />
      ) : null}
    </Wrapper>
  );
}
